import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  checkDecimalRegexTwoDigits,
  checkDigitRegex,
  checkNumericOnInputNumber,
  checkObjectsEquality,
  checkTypeStringRegexDatagrid,
  displayErrorMessage,
  displayMessage,
  getOnlyAttributeUpdatedBetweenObjects,
} from 'utils/tools_functions';
import { addParcel, updateParcel } from 'services/API/Exploitation';
import Button from '@mui/material/Button';
import {
  inputAbsolutePositiveFloatValidator,
  inputNumberRequired,
  inputRequired,
  parcelConversionDateChecker,
} from 'components/generics/Validators/validator';
import { useContext } from 'react';
import { ExploitationContext, LoadingContext } from 'utils/context';
import { validateFormData } from 'components/generics/Validators/form.validate';
import { emptyCulture } from 'utils/culturesColors';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  FormControl,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';
import { StyledTooltipErrorInputCostModal } from 'utils/Datagrid/CustomCultureDataGridUtils';
import LoadingScreen from 'components/generics/loadingScreen';
import { customDialogAddEditCommonTheme } from 'assets/styles/themes/Dialog/generic_dialog_add_edit_parcel';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { frFR } from '@mui/x-date-pickers/locales';
import { ORGANIC_STATES } from 'utils/GlobalVariables';

const DEFAULT_FORM = {
  name: '',
  surface: '',
  previousCulture1: 'EMPTY',
  previousCulture2: 'EMPTY',
  groundType: '',
  forecast: 0,
  organicState: ORGANIC_STATES.conventional,
  isIrrigable: false,
  conversionDate: null,
};

const AddEditParcelModal = ({
  modal,
  setModal,
  onSubmitSuccess,
  edit,
  maxDate,
  forecastCampaign,
  from,
  hideFormData,
}) => {
  const [formData, setFormData] = useState(edit || DEFAULT_FORM);
  const [dateError, setDateError] = useState(false);
  const [culture, setCulture] = useState(null);
  const [groundTypes, setGroundTypes] = useState(null);
  const [loadingComplete, setLoadinComplete] = useState(true);
  const { exploitationContext } = useContext(ExploitationContext);
  const setTextLoader = useContext(LoadingContext);
  const [hasChangeBioToConventional, setHasChangeBioToConventional] = useState(false);
  const [errorsFormDefault, setErrorsFormDefault] = useState({
    formError: false,
    name: {
      message: '',
      validator: [inputRequired],
    },
    surface: {
      message: '',
      validator: [inputNumberRequired, inputAbsolutePositiveFloatValidator],
    },
    conversionDate: {
      message: '',
      validator: [parcelConversionDateChecker(forecastCampaign.startYear)],
    },
  });
  const ref = useRef();
  const loadData = () => {
    try {
      setCulture([...exploitationContext?.cultures, emptyCulture]);
      setGroundTypes(exploitationContext?.groundTypes);
      setLoadinComplete(false);
    } catch (error) {
      toast.error(displayErrorMessage('ERR_UNEXPECTED'));
      setModal(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [exploitationContext]);

  useEffect(() => {
    if (culture && groundTypes) {
      const tmp = {
        groundType: formData.groundType,
      };
      if (!tmp.groundType) {
        tmp.groundType = groundTypes?.[0]?.id;
      }
      setFormData((prevFormData) => ({ ...prevFormData, ...tmp }));
    }
  }, [culture, groundTypes]);

  const onSubmit = async (e) => {
    errorsFormDefault.formError = false;
    setErrorsFormDefault({ ...validateFormData(formData, errorsFormDefault) });
    if (!errorsFormDefault.formError) {
      if (formData.previousCulture1 === 'EMPTY') {
        formData.previousCulture1 = undefined;
      }
      if (formData.previousCulture2 === 'EMPTY') {
        formData.previousCulture2 = undefined;
      }
      if (from === 'procedure') {
        if (edit) {
          onSubmitSuccess({ ...formatData(formData), id: formData?.id });
        } else {
          onSubmitSuccess({ ...formatData(formData) });
        }
      } else {
        setModal(false);
        if (e) e.preventDefault();
        if (!edit) {
          setTextLoader.customText = displayMessage('CREATE_PARCEL_LOADER');
          try {
            const res = await addParcel(exploitationContext?.id, formatData(formData));
            toast.success(`Parcelle ${formData.name} créée`);
            setFormData(DEFAULT_FORM);
            onSubmitSuccess();
          } catch (err) {
            toast.error(err.response.data['message']);
          }
        } else {
          if (!checkObjectsEquality(edit, formData)) {
            try {
              const updatedParcelBody = [{ ...formatData(formData), id: formData.id }];
              await updateParcel(exploitationContext?.id, updatedParcelBody);
              toast.success(`Parcelle ${formData.name} modifiée`);
              setFormData(DEFAULT_FORM);
              onSubmitSuccess();
            } catch (err) {
              if (err.response.data.message === `You cannot edit a child parcel.`) {
                toast.error(displayErrorMessage('ERR_UPDATE_PARCEL_CHILD'));
              } else {
                toast.error(err.response.data['message']);
              }
            }
          }
        }
      }
    }
  };

  const findName = (id, arrayToFindName) => {
    return arrayToFindName.filter((cultureItem) => cultureItem.id === id)[0]?.name;
  };

  const forecastOptions =
    culture &&
    [...culture, { name: 'Aucun prévisionnel', id: 0 }].sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );

  const formatDataForm = (updatedAttributesOnly) => {
    for (const [element, value] of Object.entries(updatedAttributesOnly)) {
      if (!updatedAttributesOnly.parcelCultures) {
        updatedAttributesOnly.parcelCultures = {};
      }
      switch (element) {
        case 'conversionDate':
          if (updatedAttributesOnly?.organicState && value === null) {
            delete updatedAttributesOnly.conversionDate;
          }
          if (updatedAttributesOnly?.organicState && value !== null) {
            delete updatedAttributesOnly.organicState;
          }
          break;
        case 'surface':
          updatedAttributesOnly[element] = +value;
          break;
        case 'previousCulture1':
          if (
            !updatedAttributesOnly.previousCulture1 &&
            updatedAttributesOnly.previousCulture1 !== 0
          ) {
            break;
          }
          updatedAttributesOnly.parcelCultures[
            `${forecastCampaign.startYear - 1}-${forecastCampaign.endYear - 1}`
          ] = {
            id: updatedAttributesOnly.previousCulture1,
            name: findName(updatedAttributesOnly.previousCulture1, culture),
            campaign: {
              startYear: forecastCampaign.startYear - 1,
              endYear: forecastCampaign.endYear - 1,
              stringFormat: `${forecastCampaign.startYear - 1}-${forecastCampaign.endYear - 1}`,
            },
          };
          delete updatedAttributesOnly.previousCulture1;
          break;
        case 'previousCulture2':
          if (
            !updatedAttributesOnly.previousCulture2 &&
            updatedAttributesOnly.previousCulture2 !== 0
          ) {
            break;
          }
          updatedAttributesOnly.parcelCultures[
            `${forecastCampaign.startYear - 2}-${forecastCampaign.endYear - 2}`
          ] = {
            id: updatedAttributesOnly.previousCulture2,
            name: findName(updatedAttributesOnly.previousCulture2, culture),
            campaign: {
              startYear: forecastCampaign.startYear - 2,
              endYear: forecastCampaign.endYear - 2,
              stringFormat: `${forecastCampaign.startYear - 2}-${forecastCampaign.endYear - 2}`,
            },
          };
          delete updatedAttributesOnly.previousCulture2;
          break;
        case 'isFixed':
        case 'forecast':
          if (updatedAttributesOnly.forecast && updatedAttributesOnly.forecast !== 0) {
            updatedAttributesOnly.parcelCultures[
              `${forecastCampaign.startYear}-${forecastCampaign.endYear}`
            ] = {
              id: updatedAttributesOnly.forecast,
              name: findName(updatedAttributesOnly.forecast, culture),
              campaign: {
                startYear: forecastCampaign.startYear,
                endYear: forecastCampaign.endYear,
                stringFormat: `${forecastCampaign.startYear}-${forecastCampaign.endYear}`,
              },
              isFixed: updatedAttributesOnly.isFixed || formData.isFixed,
            };
          }
          delete updatedAttributesOnly.forecast;
          break;
        case 'isFixed':
          if (
            updatedAttributesOnly.parcelCultures[
              `${forecastCampaign.startYear}-${forecastCampaign.endYear}`
            ]
          ) {
            updatedAttributesOnly.parcelCultures[
              `${forecastCampaign.startYear}-${forecastCampaign.endYear}`
            ].isFixed = updatedAttributesOnly.isFixed;
          } else if (formData.forecast) {
            updatedAttributesOnly.parcelCultures[
              `${forecastCampaign.startYear}-${forecastCampaign.endYear}`
            ] = { isFixed: updatedAttributesOnly.isFixed };
          }
          delete updatedAttributesOnly.isFixed;
          break;
        case 'groundType':
          if (from === 'procedure') {
            updatedAttributesOnly.groundType = {
              id: updatedAttributesOnly.groundType,
              name: findName(updatedAttributesOnly.groundType, groundTypes),
            };
          }
          break;
      }
    }
    return updatedAttributesOnly;
  };

  const formatData = (data) => {
    const dataToFormat = { ...data };
    let organicStateCheck = dataToFormat.organicState;
    // Clean up the conversion date to proceed the change of organic state
    if (hasChangeBioToConventional) {
      dataToFormat.conversionDate = null;
    }
    if (
      !hasChangeBioToConventional &&
      dayjs(dataToFormat.conversionDate).isBefore(dayjs().subtract(3, 'year'))
    ) {
      organicStateCheck = 'organic';
    } else if (formData.conversionDate) {
      organicStateCheck = null;
    }
    const updatedAttributesOnly = getOnlyAttributeUpdatedBetweenObjects(
      { ...edit },
      { ...dataToFormat }
    );

    return formatDataForm(updatedAttributesOnly);
  };

  const parentParcelName = exploitationContext?.parcels.find(
    (parcel) => parcel?.id === formData?.parentId
  )?.name;

  const updateForm = (key, value) => {
    if (key === 'organicState' && formData.organicState === 'organic') {
      // This boolean is here to keep the conversion date if the user uncheck the organic state by mistake
      setHasChangeBioToConventional(true);
    }

    setFormData({ ...formData, [key]: value });
  };

  const isOpen = useMemo(() => errorsFormDefault.formError, [errorsFormDefault]);

  const handleDateChange = (newDate) => {
    if (newDate !== null && !newDate.isValid()) {
      setDateError(true);
    } else {
      setDateError(false);
    }
    updateForm('conversionDate', newDate ? newDate?.format('YYYY-MM-DD') : null);
  };

  return (
    <ThemeProvider theme={customDialogAddEditCommonTheme}>
      <Dialog
        id="custom-addParcel-dialog"
        maxWidth="md"
        fullWidth
        open={modal}
        ref={ref}
        PaperProps={{ ref: ref }}
        onClose={() => {
          setModal(false);
        }}
      >
        <LoadingScreen open={loadingComplete}></LoadingScreen>
        <Box className="modal_header">
          <DialogTitle>{!edit ? 'Ajouter une parcelle' : 'Modifier la parcelle'}</DialogTitle>
          <ClearIcon className="close-icon" onClick={() => setModal(false)} />
        </Box>
        <DialogContent>
          <Box>
            <InputLabel id="nameLabel" htmlFor="name">
              Nom
            </InputLabel>
            {errorsFormDefault?.name.message && (
              <>
                <StyledTooltipErrorInputCostModal
                  open={isOpen}
                  title={errorsFormDefault?.name.message}
                  placement="top"
                >
                  <span></span>
                </StyledTooltipErrorInputCostModal>
              </>
            )}
            <TextField
              id="name"
              value={formData.name}
              onChange={(e) => {
                updateForm('name', checkTypeStringRegexDatagrid(e.target.value));
              }}
              placeholder="Entrez un nom"
              disabled={formData.parentId && !formData.isParent}
              className={errorsFormDefault?.name.message ? 'MuiErrorForm' : ''}
            />
          </Box>
          <Box>
            <InputLabel id="surface" htmlFor="surface">
              Surface
            </InputLabel>
            {errorsFormDefault?.surface.message && (
              <>
                <StyledTooltipErrorInputCostModal
                  open={isOpen}
                  title={errorsFormDefault?.surface.message}
                  placement="top"
                >
                  <span></span>
                </StyledTooltipErrorInputCostModal>
              </>
            )}
            <TextField
              id="surface"
              value={formData.surface}
              onKeyDown={(e) => checkNumericOnInputNumber(e)}
              onChange={(e) => {
                if (checkDecimalRegexTwoDigits(e) && checkDigitRegex(e)) {
                  updateForm('surface', e.target.value);
                } else {
                  e.preventDefault();
                }
              }}
              placeholder="Entrez la surface"
              disabled={formData.parentId && !formData.isParent}
              className={errorsFormDefault?.surface.message ? 'MuiErrorForm' : ''}
              inputProps={{
                'data-type': 'number',
              }}
            />
          </Box>
          {culture && groundTypes && (
            <>
              <Box>
                <InputLabel name="previousCulture1">
                  {forecastCampaign.startYear - 1} - {forecastCampaign.endYear - 1}
                </InputLabel>
                <Select
                  id="previousCulture1"
                  sx={{ width: '300px' }}
                  onChange={(e) => updateForm('previousCulture1', e.target.value)}
                  value={formData.previousCulture1 ?? 'EMPTY'}
                  disabled={formData.parentId && !formData.isParent}
                >
                  {culture.map((cultureItem) => (
                    <MenuItem key={cultureItem?.id} value={cultureItem?.id}>
                      <ListItemText primary={cultureItem.name} />
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box>
                <InputLabel name="previousCulture2">
                  {forecastCampaign.startYear - 2} - {forecastCampaign.endYear - 2}
                </InputLabel>
                <FormControl>
                  <Select
                    id="previousCulture2"
                    sx={{ width: '300px' }}
                    onChange={(e) => updateForm('previousCulture2', e.target.value)}
                    value={formData.previousCulture2 ?? 'EMPTY'}
                    disabled={formData.parentId && !formData.isParent}
                  >
                    {culture.map((cultureItem) => (
                      <MenuItem key={cultureItem?.id} value={cultureItem?.id}>
                        <ListItemText primary={cultureItem.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <InputLabel name="groundType">Type de sol</InputLabel>
                <Select
                  id="groundType"
                  sx={{ width: '300px' }}
                  onChange={(e) => updateForm('groundType', e.target.value)}
                  value={formData.groundType}
                  disabled={formData.parentId && !formData.isParent}
                >
                  {groundTypes.map((groundTypeItem) => (
                    <MenuItem key={groundTypeItem?.id} value={groundTypeItem?.id}>
                      <ListItemText primary={groundTypeItem.name} />
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              {hideFormData.length >= 0 && !hideFormData.includes('forecast') && (
                <Box>
                  <InputLabel name="forecast">
                    Prévisionnel ({forecastCampaign.startYear}-{forecastCampaign.endYear})
                  </InputLabel>
                  <Select
                    id="forecast"
                    sx={{ width: '300px' }}
                    onChange={(e) => updateForm('forecast', e.target.value)}
                    value={formData.forecast}
                    disabled={formData.parentId && !formData.isParent}
                  >
                    {forecastOptions.map((forecast) => (
                      <MenuItem key={forecast?.id} value={forecast?.id}>
                        <ListItemText primary={forecast.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
              <Box>
                <InputLabel name="organicState">Bio</InputLabel>
                <Checkbox
                  className="checkbox-irrigable dialog-checkbox"
                  id="irrigable"
                  label="irrigable"
                  checked={formData.organicState === ORGANIC_STATES.organic}
                  disabled={formData.parentId && !formData.isParent}
                  onChange={() => {
                    updateForm(
                      'organicState',
                      formData.organicState === ORGANIC_STATES.organic
                        ? ORGANIC_STATES.conventional
                        : ORGANIC_STATES.organic
                    );
                  }}
                />
              </Box>
              <Box>
                <InputLabel name="conversionDate" htmlFor="conversionDate">
                  Date de conversion
                </InputLabel>
                {errorsFormDefault?.conversionDate.message && (
                  <>
                    <StyledTooltipErrorInputCostModal
                      open={isOpen}
                      title={errorsFormDefault?.conversionDate.message}
                      placement="top"
                    >
                      <span></span>
                    </StyledTooltipErrorInputCostModal>
                  </>
                )}
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="fr"
                  localeText={frFR.components.MuiLocalizationProvider.defaultProps.localeText}
                >
                  <DatePicker
                    id="conversionDate"
                    value={dayjs(formData.conversionDate)}
                    disabled={formData.parentId || formData.isParent}
                    maxDate={maxDate}
                    onChange={handleDateChange}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        error: dateError,
                        helperText: dateError ? 'Date invalide' : '',
                      },
                      field: { clearable: true },
                      clearButton: {
                        className: 'custom-clear-button',
                      },
                      yearButton: {
                        sx: {
                          padding: '0',
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <Box>
                <InputLabel name="irrigable">Irrigable</InputLabel>
                <Checkbox
                  className="checkbox-irrigable dialog-checkbox"
                  id="irrigable"
                  label="irrigable"
                  checked={!!formData.isIrrigable}
                  disabled={formData.parentId && !formData.isParent}
                  onChange={() => {
                    updateForm('isIrrigable', !formData.isIrrigable);
                  }}
                />
              </Box>
              {hideFormData.length >= 0 && !hideFormData.includes('isFixed') && (
                <Box>
                  <InputLabel name="isFixed">Prévisionnel fixé</InputLabel>
                  <Checkbox
                    id="isFixed"
                    label="isFixed"
                    checked={!!formData.isFixed}
                    disabled={formData.parentId && !formData.isParent}
                    onChange={() => {
                      updateForm('isFixed', !formData.isFixed);
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        {formData.parentId && (
          <p className="text-info-parcel">Cette parcelle est regroupée avec {parentParcelName}</p>
        )}
        {formData.isParent && (
          <p className="text-info-parcel">Cette parcelle est parente d'un regroupement</p>
        )}
        <DialogActions>
          <ThemeProvider theme={customButtonTheme}>
            <Button id="addParcelButton" onClick={onSubmit}>
              {!edit ? 'Ajouter' : 'Enregistrer'}
            </Button>
          </ThemeProvider>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AddEditParcelModal;
